import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { FirAnalyzeCurrentTaxesService } from './fir-analyze-current-taxes.service';

@Component({
    selector: 'gtw-fir-analyze-current-taxes',
    templateUrl: './fir-analyze-current-taxes.component.html',
    styleUrls: ['./fir-analyze-current-taxes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirAnalyzeCurrentTaxesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly contextSubs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private isInitialized = false;
  isLoading = false;

  title = 'Comments';
  minimise = false;
  COMMENTS = '';
  allComments: any[] = [];
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  isSaveClicked = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private service: FirAnalyzeCurrentTaxesService,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextSubs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.getAllComments();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  save(): void {
    if (!this.COMMENTS || !this.COMMENTS.trim()) {
      this.gtwSnackbarService.openSnackBar('Please enter a valid comments', 'gtw-snackbar--red', true, 4);
      return;
    }
    if (this.COMMENTS.length > 2000) {
      this.gtwSnackbarService.openSnackBar('Please comments entered cannot be more than 2000.', 'gtw-snackbar--red', true, 4);
      return;
    }
    if (this.isSaveClicked) {
      return;
    }
    if (!this.GENERAL_CONFIG?.base_url) {
      this.gtwSnackbarService.openSnackBar('Error occurred while saving.', 'gtw-snackbar--red', true, 4);
      return;
    }

    this.isSaveClicked = true;
    this.isLoading = true;
    this.cdr.detectChanges();

    const globalParamsForSave = this.globalService?.globalParams || this.globalService || {};
    this.service
      .saveComment(this.GENERAL_CONFIG.base_url, this.rowData, this.COMMENTS, globalParamsForSave, this.filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          this.isLoading = false;
          this.isSaveClicked = false;
          if (result?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green', true, 4);
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(result?.errorMessage || 'Error occurred while saving.', 'gtw-snackbar--red', true, 4);
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isLoading = false;
          this.isSaveClicked = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while saving.', 'gtw-snackbar--red', true, 4);
          this.cdr.detectChanges();
        }
      );
  }

  private getAllComments(): void {
    if (!this.GENERAL_CONFIG?.base_url) {
      this.allComments = [];
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }
    this.isLoading = true;
    const gp = this.globalService?.globalParams || this.globalService || {};
    const params = {
      tax_year: this.filterParams?.tax_year || gp?.tax_year,
      scenario: this.filterParams?.scenario || gp?.scenario,
      jcd_key: this.rowData?.JCD_KEY || this.filterParams?.jcd_key || gp?.jcd_key || 250,
      combination_key: this.rowData?.COMBINATION_KEY,
      sso_id: this.USER_SETTINGS?.user?.sso_id,
    };

    this.service.loadComments(this.GENERAL_CONFIG.base_url, params).pipe(takeUntil(this.destroy$)).subscribe(
      (data: any) => {
        this.allComments = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.allComments = [];
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }
}
